import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, ShoppingBag, ArrowLeft } from "lucide-react";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, loading } = useAuth();
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    // Wait for auth to finish loading before checking payment
    if (loading) return;
    
    // Get token from context or fallback to localStorage
    const authToken = token || localStorage.getItem("token");
    
    if (sessionId && authToken) {
      pollPaymentStatus(0, authToken);
    } else if (!sessionId) {
      setStatus("error");
    }
  }, [sessionId, token, loading]);

  const pollPaymentStatus = async (attempts, authToken) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
        withCredentials: true
      });

      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1, authToken), pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />

      <main className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl p-8 border border-[#E2E8F0] text-center" data-testid="payment-result">
          {status === "loading" && (
            <>
              <Loader2 size={64} className="mx-auto text-[#475569] animate-spin mb-6" />
              <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Verificando pago...</h1>
              <p className="text-[#64748B]">Espera mientras confirmamos tu pago</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto bg-[#10B981]/10 rounded-full flex items-center justify-center mb-6">
                <CheckCircle size={48} className="text-[#10B981]" />
              </div>
              <h1 className="text-2xl font-bold text-[#0F172A] mb-2">¡Pago exitoso!</h1>
              <p className="text-[#64748B] mb-6">
                Tu pago de ${paymentData?.amount?.toFixed(2)} ha sido procesado correctamente.
              </p>
              <div className="space-y-3">
                {paymentData?.product_id && (
                  <Link to={`/product/${paymentData.product_id}`}>
                    <Button className="w-full rounded-full bg-[#475569] hover:bg-[#334155]" data-testid="view-product">
                      <ShoppingBag size={18} className="mr-2" />
                      Ver producto
                    </Button>
                  </Link>
                )}
                <Link to="/">
                  <Button variant="outline" className="w-full rounded-full" data-testid="go-home">
                    Volver al inicio
                  </Button>
                </Link>
              </div>
            </>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <>
              <div className="w-20 h-20 mx-auto bg-[#EF4444]/10 rounded-full flex items-center justify-center mb-6">
                <XCircle size={48} className="text-[#EF4444]" />
              </div>
              <h1 className="text-2xl font-bold text-[#0F172A] mb-2">
                {status === "expired" ? "Sesión expirada" : "Error en el pago"}
              </h1>
              <p className="text-[#64748B] mb-6">
                {status === "expired" 
                  ? "La sesión de pago ha expirado. Por favor intenta nuevamente."
                  : "No pudimos verificar tu pago. Por favor contacta soporte si el problema persiste."}
              </p>
              <Link to="/">
                <Button className="w-full rounded-full bg-[#475569] hover:bg-[#334155]">
                  <ArrowLeft size={18} className="mr-2" />
                  Volver al inicio
                </Button>
              </Link>
            </>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default PaymentSuccessPage;
